import React from 'react';
import {
  Alert, AlertDescription, AlertIcon, Badge, Box, Button, Checkbox, Code, Flex,
  IconButton, Input, InputGroup, InputLeftElement, List, ListItem, Text, Textarea,
} from '@chakra-ui/react';
import { CloseIcon, SearchIcon } from '../../icons';
import { countWord, shortTenantId } from '../../utils/tenantAccess';
import StatusAlert from '../ElementaryComponents/StatusAlert';
import {
  EmptyState, NAVY, Panel, StepFooter, navyPress,
} from './TenantAccessLayout';

const TenantTab = ({
  mode,
  onMode,
  pasteText,
  onPasteText,
  onCheck,
  checking,
  checkError,
  checkSummary,
  invalid,
  onClearInvalid,
  query,
  onQueryChange,
  results,
  isFetching,
  isError,
  error,
  selected,
  onToggle,
  onRemove,
  onClear,
  canContinue,
  onBack,
  onContinue,
}) => {
  const hint = invalid.length
    ? 'Fix or clear the invalid IDs to continue.'
    : (selected.length ? '' : 'Add at least one tenant to continue.');

  let finder = null;
  if (mode === 'paste') {
    finder = (
      <Box>
        <Text as="label" htmlFor="pasteText" fontWeight="600" fontSize="sm" display="block" mb={2}>Tenant IDs</Text>
        <Textarea
          id="pasteText"
          value={pasteText}
          onChange={(event) => onPasteText(event.target.value)}
          fontFamily="mono"
          fontSize="sm"
          minH="130px"
          placeholder={'Paste one ID per line, or separate with commas\na56d5d02-c1eb-489f-9fae-a6b22343d009'}
        />
        <Flex gap={3} align="center" mt={2} wrap="wrap">
          <Button variant="outline" colorScheme="blue" onClick={onCheck} isLoading={checking}>Check IDs</Button>
          <Text fontSize="xs" color="gray.600">
            We match each ID to a district or charter. Valid ones move to the list below;
            anything we can’t match stays here for you to fix.
          </Text>
        </Flex>
        {checkError && <Box mt={3}><StatusAlert status="error" alert_title="Could not check IDs" error={checkError} /></Box>}
        {checkSummary && (checkSummary.added_count > 0 || checkSummary.skipped_count > 0) && (
          <Alert status="success" mt={3} borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              {checkSummary.added_count
                ? `${countWord(checkSummary.added_count, 'tenant')} added.`
                : 'No new tenants added.'}
              {checkSummary.skipped_count
                ? ` ${countWord(checkSummary.skipped_count, 'duplicate')} ${checkSummary.skipped_count === 1 ? 'was' : 'were'} skipped.`
                : ''}
            </AlertDescription>
          </Alert>
        )}
        {invalid.length > 0 && (
          <Alert status="error" mt={3} borderRadius="md" alignItems="flex-start" role="alert">
            <AlertIcon />
            <Box>
              <Text fontWeight="700">{`${countWord(invalid.length, 'ID')} couldn’t be added`}</Text>
              <Text>Fix them in the box above and check again, or clear them to continue.</Text>
              <List mt={2} spacing={2}>
                {invalid.map((item) => (
                  <ListItem key={item.id} display="flex" gap={2} wrap="wrap">
                    <Code fontSize="xs">{item.id}</Code>
                    <Text>{item.message}</Text>
                  </ListItem>
                ))}
              </List>
              <Button mt={3} size="sm" variant="outline" colorScheme="red" onClick={onClearInvalid}>
                Clear invalid IDs
              </Button>
            </Box>
          </Alert>
        )}
      </Box>
    );
  } else {
    const trimmed = query.trim();
    let searchResults = (
      <Text fontSize="sm" color="gray.600">Start typing a name, county-district number, or ID.</Text>
    );
    if (isFetching) {
      searchResults = <StatusAlert status="loading" alert_title="Searching tenants" />;
    } else if (isError) {
      searchResults = <StatusAlert status="error" alert_title="Could not search tenants" error={error} />;
    } else if (trimmed && results) {
      const selectedInResults = results.filter((tenant) => (
        selected.some((item) => item.id === tenant.id)
      )).length;
      searchResults = results.length ? (
        <Box borderWidth="1px" borderRadius="md" maxH="300px" overflow="auto">
          <Flex px={3} py={2} bg="gray.50" borderBottomWidth="1px" justify="space-between" fontSize="xs" fontWeight="600" color="gray.600">
            <Text>{countWord(results.length, 'result')}</Text>
            <Text>{`${selectedInResults} selected`}</Text>
          </Flex>
          {results.map((tenant) => (
            <Checkbox
              key={tenant.id}
              isChecked={selected.some((item) => item.id === tenant.id)}
              onChange={() => onToggle(tenant)}
              width="100%"
              px={3}
              py={2}
              borderBottomWidth="1px"
              _last={{ borderBottomWidth: 0 }}
            >
              <Text fontWeight="600">{tenant.name}</Text>
              <Text fontSize="xs" color="gray.600">
                {`CDN ${tenant.cdn} · ${tenant.type} · `}
                <Text as="span" fontFamily="mono">{shortTenantId(tenant.id)}</Text>
              </Text>
              {tenant.inactive && <Badge colorScheme="yellow">Inactive</Badge>}
            </Checkbox>
          ))}
        </Box>
      ) : (
        <Box borderWidth="1px" borderRadius="md">
          <EmptyState
            title={`No districts or charters match “${query}”`}
            text="Try part of the name, the county-district number, or paste the ID instead."
          />
        </Box>
      );
    }
    finder = (
      <Box>
        <Text as="label" htmlFor="tenantQuery" fontWeight="600" fontSize="sm" display="block" mb={2}>
          Find districts and charters
        </Text>
        <InputGroup>
          <InputLeftElement pointerEvents="none"><SearchIcon color="gray.500" /></InputLeftElement>
          <Input
            id="tenantQuery"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Name, county-district number, or ID"
            autoComplete="off"
          />
        </InputGroup>
        <Box mt={3}>{searchResults}</Box>
      </Box>
    );
  }

  const selectedBlock = selected.length ? (
    <Box borderWidth="1px" borderRadius="md" overflow="hidden">
      <Flex px={3} py={2} bg="gray.50" borderBottomWidth="1px" justify="space-between" align="center">
        <Text fontSize="xs" fontWeight="600" color="gray.600">{`Selected tenants (${selected.length})`}</Text>
        <Button variant="link" size="sm" colorScheme="blue" onClick={onClear}>Clear all</Button>
      </Flex>
      {selected.map((tenant) => (
        <Flex key={tenant.id} align="center" gap={3} px={3} py={2} borderBottomWidth="1px" _last={{ borderBottomWidth: 0 }}>
          <Box flex="1" minW="0">
            <Text fontWeight="600">
              {`${tenant.name} `}
              <Badge>{tenant.type}</Badge>
              {tenant.inactive && <Badge ml={2} colorScheme="yellow">Inactive</Badge>}
            </Text>
            <Text fontSize="xs" color="gray.600">
              {`CDN ${tenant.cdn} · `}
              <Text as="span" fontFamily="mono">{tenant.id}</Text>
            </Text>
          </Box>
          <IconButton
            aria-label={`Remove ${tenant.name}`}
            icon={<CloseIcon />}
            size="sm"
            variant="ghost"
            onClick={() => onRemove(tenant.id)}
          />
        </Flex>
      ))}
    </Box>
  ) : (
    <Box borderWidth="1px" borderRadius="md">
      <EmptyState
        title="No tenants selected yet"
        text={mode === 'paste'
          ? 'Paste tenant IDs above and select Check IDs.'
          : 'Search above and tick the districts or charters to add.'}
      />
    </Box>
  );

  return (
    <Panel
      title="Where should they be added?"
      subtitle="Paste the tenant IDs you already have, or search for districts and charters by name."
      footer={(
        <StepFooter
          showBack
          hint={hint}
          canContinue={canContinue}
          onBack={onBack}
          onContinue={onContinue}
        />
      )}
    >
      <Flex
        display="inline-flex"
        borderWidth="1px"
        borderRadius="md"
        p={1}
        bg="gray.50"
        gap={1}
        role="tablist"
      >
        <Button
          size="sm"
          variant={mode === 'paste' ? 'solid' : 'ghost'}
          colorScheme="blue"
          color={mode === 'paste' ? NAVY : undefined}
          {...navyPress}
          onClick={() => onMode('paste')}
        >
          Paste tenant IDs
        </Button>
        <Button
          size="sm"
          variant={mode === 'search' ? 'solid' : 'ghost'}
          colorScheme="blue"
          color={mode === 'search' ? NAVY : undefined}
          {...navyPress}
          onClick={() => onMode('search')}
        >
          Search by name
        </Button>
      </Flex>
      {finder}
      {selectedBlock}
    </Panel>
  );
};

export default TenantTab;
